import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "../atoms/spinner";
import { UserModal } from "../organisms/UserModal";
import { ToggleUserStatusAlert } from "../organisms/ToggleUserStatusAlert";
import { UserManagementTable } from "../organisms/UserManagementTable";
import { UserStatus } from "../config/types";
import useUsers from "../api/swr/useUsers";
import { type User } from "../api/types";
import SearchBar from "~/molecules/SearchBar";
import { ConfirmDialog } from "~/molecules/ConfirmDialog";
import { includesSearch } from "~/helpers/string";
import { deleteUser } from "~/api/v2/requests";
import { getUserDocumentRoles } from "~/api/v2/useUserDocumentRoles";

export const UserManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("search") ?? "";

  const { data, isLoading, mutate } = useUsers({
    status: [UserStatus.active, UserStatus.pending, UserStatus.deactivated],
  });

  const filteredUsers = useMemo(() => {
    if (!data || !search) return data ?? [];
    return data.filter(
      (user) =>
        includesSearch(`${user.firstName} ${user.lastName ?? ""}`, search) ||
        includesSearch(user.email, search),
    );
  }, [data, search]);

  const [showUserModal, setShowUserModal] = useState(false);
  const [showUserStatusAlert, setShowUserStatusAlert] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [userData, setUserData] = useState<User>();

  const onClose = () => {
    setUserData(undefined);
    setShowUserModal(false);
    setShowUserStatusAlert(false);
    setShowDeleteAlert(false);
  };

  const onEditUser = (user: User) => {
    setShowUserModal(true);
    setUserData(user);
  };

  const onToggleUserStatus = async (user: User) => {
    if (user.status === UserStatus.active) {
      try {
        const roles = await getUserDocumentRoles(user.id);
        if (roles.ownerIn.length > 0 || roles.publisherIn.length > 0) {
          void navigate(`/users/${user.id}/offboard`);
          return;
        }
      } catch {
        toast.error(t(($) => $.toasts.error.generic));
        return;
      }
    }
    setShowUserStatusAlert(true);
    setUserData(user);
  };

  const onDeleteUser = (user: User) => {
    setShowDeleteAlert(true);
    setUserData(user);
  };

  const onConfirmDelete = async () => {
    if (!userData) return;
    try {
      await deleteUser(userData.id);
      void mutate();
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    } finally {
      onClose();
    }
  };

  const setSearch = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set("search", value);
    } else {
      params.delete("search");
    }
    setSearchParams(params);
  };

  return (
    <>
      <div className="container pb-8">
        <div className="flex items-center justify-between py-7">
          <h3 className="text-4xl font-semibold">
            {t(($) => $.settings.userManagement.title)}
          </h3>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <SearchBar
              placeholder={t(($) => $.settings.userManagement.search)}
              value={search}
              onDebouncedSearch={setSearch}
              onSearch={setSearch}
              onClearSearch={() => setSearch("")}
            />
            <Button onClick={() => setShowUserModal(true)}>
              {t(($) => $.commons.buttons.addUser)}
            </Button>
          </div>

          {isLoading ? (
            <CenterSpinner withText />
          ) : (
            <UserManagementTable
              users={filteredUsers}
              onEditUser={onEditUser}
              onToggleUserStatus={onToggleUserStatus}
              onDeleteUser={onDeleteUser}
            />
          )}
        </div>
      </div>

      <UserModal
        isOpen={showUserModal}
        userData={userData}
        onSubmit={mutate}
        onClose={onClose}
      />

      <ToggleUserStatusAlert
        isOpen={showUserStatusAlert}
        userData={userData}
        onSuccess={mutate}
        onClose={onClose}
      />

      <ConfirmDialog
        open={showDeleteAlert}
        onOpenChange={(open) => !open && onClose()}
        title={t(($) => $.settings.userManagement.deleteUser)}
        description={t(($) => $.settings.userManagement.deleteUserSupText)}
        onConfirm={onConfirmDelete}
      />
    </>
  );
};
